import { PeTypes } from '@platform/types';
import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

const URL = process.env.NX_REALTIME_SERVICE_URL as string;
export type RealTimeEvent =
  | 'pong'
  | 'load'
  | 'jobCompleted'
  | 'jobCreated'
  | 'jobQueued'
  | 'jobUpdated'
  | 'notificationCreated';
export type RealTimeEventListener = (message: PeTypes.GeoBufferJobNotification) => void;

const useWebSocket = () => {
  const webSocketRef = useRef<WebSocket | null>();
  const listenersRef = useRef<Record<string, RealTimeEventListener[]>>({});

  useEffect(() => {
    return () => {
      close();
      listenersRef.current = {};
    };
  }, []);

  const initialize = (userId: number) => {
    console.info('useWebSocket.initialize:', userId);

    try {
      // first close, if existing...
      close();

      // Create a new WebSocket instance when the component mounts
      const webSocket = new WebSocket(URL);

      // Set up event listeners for the WebSocket instance
      webSocket.onopen = () => {
        console.info('WebSocket connection opened.');

        const realTimeNotificationPing = {
          messageId: uuidv4(),
          context: 'geobuffer',
          type: 'notification',
          action: 'ping',
          userId: userId,
        };
        const realTimeNotificationStart = {
          messageId: uuidv4(),
          context: 'geobuffer',
          type: 'notification',
          action: 'start',
          userId: userId,
        };

        const realTimeJobPing = {
          messageId: uuidv4(),
          context: 'geobuffer',
          type: 'job',
          action: 'ping',
          userId: userId,
        };
        const realTimeJobStart = {
          messageId: uuidv4(),
          context: 'geobuffer',
          type: 'job',
          action: 'start',
          userId: userId,
        };

        if (webSocket) {
          webSocket.send(JSON.stringify(realTimeNotificationPing));
          webSocket.send(JSON.stringify(realTimeNotificationStart));
          webSocket.send(JSON.stringify(realTimeJobPing));
          webSocket.send(JSON.stringify(realTimeJobStart));
        }
      };
      webSocketRef.current = webSocket;
    } catch (error) {
      console.error('There was an error while setting up websockets', { error });
    }
  };

  useEffect(() => {
    if (!webSocketRef.current) return;
    webSocketRef.current.onmessage = (event) => {
      // Received a message from the server.
      const jsonData = JSON.parse(event.data);
      const actionType = jsonData.action;

      if (listenersRef.current[actionType]) {
        listenersRef.current[actionType].forEach((listener) => listener(jsonData.notification));
      }
    };

    webSocketRef.current.onclose = () => console.info('WebSocket connection closed.');
    webSocketRef.current.onerror = (error) => console.info('WebSocket error:', error);
  }, [webSocketRef.current]);

  const close = () => {
    try {
      if (!webSocketRef.current) return;

      const { readyState } = webSocketRef.current;

      if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
        webSocketRef.current.close();
      }
    } catch (e) {
      console.error('useWebSocket.close:', e);
    }
  };

  const registerListener = (eventType: RealTimeEvent, listener: RealTimeEventListener) => {
    if (!listenersRef.current[eventType]) {
      listenersRef.current[eventType] = [];
    }

    // avoid double listeners
    if (!listenersRef.current[eventType].includes(listener)) {
      listenersRef.current[eventType].push(listener);
    }

    return () => {
      // the unregister handle
      const index = listenersRef.current[eventType].findIndex((x) => x === listener);
      if (index !== -1) {
        // Removing the item from the array
        listenersRef.current[eventType].splice(index, 1);
      }
    };
  };

  return { initialize, close, registerListener };
};

export default useWebSocket;
